/**
 * admin-review-detail.js — 詳情審核頁「決策處理」接線層（卷宗 B）
 *
 * 7 頁共用一支：consult-detail / platform-{clinic,data,license,article,comment,treatment}-review-detail。
 *（2026-07-16 校正：原漏列 treatment 頁，與 docs/BACKEND_HANDOFF.md#admin-review-decision 標題
 *   互相矛盾——現統一為實際綁定集合，見該檔案的列舉校正註記。）
 * 載入：由 /src/admin.js 中央 import 鏈 auto-init，無對應 DOM 即 no-op（不影響其餘 admin/auth 頁）。
 * ⚠ 純接線、無真送出：platform-* 審核營運在山川 CMS，本層只做退回驗證 / toast / 狀態翻轉 / 終態鎖示意。
 *
 * 只接線、零視覺改：
 *   - Modal：一律用 modal.js 既有 API（Modal.close），不自寫 show/hide。
 *   - Toast：一律用 admin-toast.js 既有 API（textContent 防 XSS），不自繪。
 *   - 欄位 error：沿用 form-field atom 既有節點（aria-invalid + .form-field--invalid +
 *     .form-field__error[role=alert][hidden]），輔助函式 setInvalid/clearInvalid 鏡像
 *     member-edit.js / auth-forms.js 慣例，不新增 class。
 *   - 狀態 pill / 終態 disabled：套既有 .admin-pill--*（admin-dossier.css）與 [disabled]，零 hardcode hex。
 *
 * 接線邏輯（規格卷宗 B）：
 *   1 退回必填驗證：confirm 鈕 → textarea.trim().length >= MIN（article/comment 另驗分類 select
 *     非空）。失敗 → 標紅 + focus + 不關 modal；textarea input 達標即時清 error。
 *   2 決策成功回饋：合格 → confirm 鈕 disabled +「處理中…」(一幀過場) → Modal.close()
 *     → AdminToast.show(success)（退回帶 undo + countdown）→ page-head badge 換 class/文字
 *     → .review-action__btn 全 disabled 進終態。
 *   3 consult 回覆：驗 ≥1 字 → 插入 .reply-history__item.is-entering → toast（純 textarea）。
 *   4 內部備註：note-form submit → toast。
 *   5 返回保留篩選：MVP 簡作（讀 sessionStorage 還原 querystring），詳見 initBackLink 註解。
 *
 * 決策 confirm 鈕原帶 data-modal-dismiss（靜態頁 demo 用 modal.js 直接關）。本層接管後
 * 於 init 時移除該屬性，改由 JS 控制關閉時機（退回驗證失敗時必須「不關」）。
 */

import { Modal } from './modal.js';
import { setInvalid, clearInvalid } from './field-error.js';
import { prefersReducedMotion } from '../utils/dom.js';

/* ============================ 共用工具 ============================ */

/* 欄位 invalid 標記 → 收斂至 field-error.js（單一真相源）。本層一律不傳 message（null 路徑）：
 * 沿用 HTML 既有 error 節點文案、且不憑空建節點（無節點的 select 僅標紅框 + focus，與舊行為等價）。
 * 新簽名：setInvalid(control) / clearInvalid(control)，原本傳入的 errorEl 改由 helper 自解。 */

/**
 * 取 modal footer 內的「確認」鈕（結構定位，免依賴各頁不一致的 id）：
 * 非 .button--ghost 的那顆（取消鈕一律 ghost）。decision modal footer 固定兩鈕。
 */
function getConfirmBtn(modalEl) {
  if (!modalEl) return null;
  const footer = modalEl.querySelector('.modal__footer');
  if (!footer) return null;
  const btns = footer.querySelectorAll('button');
  for (const b of btns) {
    if (!b.classList.contains('button--ghost')) return b;
  }
  return null;
}

/**
 * 進入終態：page-head 狀態 pill 換語意 + .review-action__btn 全 disabled。
 * @param {'approve'|'reject'|'unpublish'} outcome
 *
 * 狀態 pill 的真實 class 是 .admin-pill（admin-dossier.css），非 .badge。
 * 可用變體只有 --active / --pending / --rejected / --filed（badge--success 等不適用於 admin-pill）：
 *   approve   → admin-pill--active（teal「通過/已核准」正向終態）
 *   reject    → admin-pill--rejected（red「已退回」負向終態）
 *   unpublish → admin-pill--filed（grey「已下架」歸檔終態）
 */
const PILL_BY_OUTCOME = {
  approve: { cls: 'admin-pill--active', text: '已通過' },
  reject: { cls: 'admin-pill--rejected', text: '已退回' },
  unpublish: { cls: 'admin-pill--filed', text: '已下架' },
};
const ALL_PILL_MODS = ['admin-pill--active', 'admin-pill--pending', 'admin-pill--rejected', 'admin-pill--filed'];

function applyTerminalState(root, outcome) {
  // page-head 狀態 pill（右上）。選擇器限定 .admin-page-head__actions 內，
  // 避免動到 article/comment 頁 info-grid 值欄裡的第二顆 .admin-pill。
  const pill = root.querySelector('.admin-page-head__actions .admin-pill');
  const map = PILL_BY_OUTCOME[outcome];
  // 找不到目標 pill 是 bug（5 頁皆有），非正常 no-op：開發環境留警示，不靜默吞。
  if (!pill) {
    if (import.meta.env && import.meta.env.DEV) {
      console.warn('[admin-review-detail] 找不到 .admin-page-head__actions .admin-pill，狀態無法更新');
    }
  } else if (map) {
    // 首次進終態時記下原始狀態（class + 文字），供 undo 精準還原。
    // 各頁初始 pill 文字不一（送審中 / 待審核 / 待審），不可用固定字還原。
    if (pill.dataset.originalPillClass === undefined) {
      const originalMod = ALL_PILL_MODS.find((m) => pill.classList.contains(m)) || 'admin-pill--pending';
      pill.dataset.originalPillClass = originalMod;
      pill.dataset.originalPillText = pill.textContent;
    }
    ALL_PILL_MODS.forEach((m) => pill.classList.remove(m));
    pill.classList.add(map.cls);
    pill.textContent = map.text;
  }
  // 審核動作鈕全部進終態。進終態前記住「原本就 disabled」者（如未上線時的下架鈕），
  // 供 undo 時精準還原（原 disabled 維持 disabled，非全部解鎖）。
  root.querySelectorAll('.review-action__btn').forEach((btn) => {
    if (btn.dataset.wasDisabled === undefined) {
      btn.dataset.wasDisabled = btn.disabled ? '1' : '0';
    }
    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
  });
}

/**
 * confirm 鈕「處理中…」一幀過場 → 回呼 done()。
 * disabled + 改字（保留原字供 reduced-motion / 還原無需），下一幀執行 done。
 */
function withProcessing(btn, done) {
  if (!btn) { done(); return; }
  btn.disabled = true;
  btn.setAttribute('aria-busy', 'true');
  const original = btn.textContent;
  btn.dataset.originalLabel = original;
  btn.textContent = '處理中…';
  // 一幀過場（reduced-motion 下也走一個 rAF，視覺上等同瞬時）
  requestAnimationFrame(() => {
    requestAnimationFrame(() => done());
  });
}

/* ============================ 決策（核准 / 退回 / 下架）============================ */

/**
 * 綁一個「決策」動作（核准 / 退回 / 下架其一）。
 * @param {Object} cfg
 * @param {HTMLElement} cfg.root          頁面根（document）
 * @param {string} cfg.modalSel           該決策 modal 選擇器
 * @param {'approve'|'reject'|'unpublish'} cfg.outcome
 * @param {Object} cfg.toast              { title, text }
 * @param {Object} [cfg.validate]         退回類才有：{ textarea, min, selectEl? }
 *   （error 節點由 field-error.js 依 .form-field 包裹自解，不再傳入；select 無節點時僅標紅框 + focus）
 */
function bindDecision(cfg) {
  const modalEl = cfg.root.querySelector(cfg.modalSel);
  if (!modalEl) return;
  const confirmBtn = getConfirmBtn(modalEl);
  if (!confirmBtn) return;

  // 接管關閉時機：移除 data-modal-dismiss，改由本層控制（退回失敗須不關）。
  confirmBtn.removeAttribute('data-modal-dismiss');

  const v = cfg.validate;

  // 退回類：textarea / select 即時清 error（達標就清）
  if (v && v.textarea) {
    v.textarea.addEventListener('input', () => {
      if (v.textarea.value.trim().length >= v.min) clearInvalid(v.textarea);
    });
  }
  if (v && v.selectEl) {
    v.selectEl.addEventListener('change', () => {
      if (v.selectEl.value) clearInvalid(v.selectEl);
    });
  }

  confirmBtn.addEventListener('click', () => {
    // ---- 退回必填驗證 ----
    if (v && v.textarea) {
      let ok = true;
      let firstInvalid = null;
      // 分類 select（article/comment）：非空。此頁 select 無獨立 error 節點 → null 路徑只標紅框 + focus。
      if (v.selectEl && !v.selectEl.value) {
        setInvalid(v.selectEl);
        firstInvalid = firstInvalid || v.selectEl;
        ok = false;
      }
      if (v.textarea.value.trim().length < v.min) {
        setInvalid(v.textarea);
        firstInvalid = firstInvalid || v.textarea;
        ok = false;
      }
      if (!ok) {
        if (firstInvalid) firstInvalid.focus();
        return; // 不關 modal
      }
    }

    // ---- 合格 → 成功回饋 ----
    // TODO(backend): bindDecision 成功分支改打審核 API（核准/退回/下架寫狀態+通知），終態由後端驅動；回覆/備註持久化；釐清山川 CMS 邊界 — 見 docs/BACKEND_HANDOFF.md#admin-review-decision
    withProcessing(confirmBtn, () => {
      Modal.close(modalEl); // 焦點自動返還觸發鈕（modal.js）
      const toastOpts = {
        type: 'success',
        title: cfg.toast.title,
        text: cfg.toast.text,
      };
      // 退回：帶「復原」undo + 倒數條（成功回饋規格 2）。MVP 復原僅還原前端終態。
      if (cfg.outcome === 'reject') {
        toastOpts.countdown = true;
        toastOpts.action = {
          label: '復原',
          onClick: () => undoTerminalState(cfg.root),
        };
      }
      window.AdminToast.show(toastOpts);
      applyTerminalState(cfg.root, cfg.outcome);
    });
  });
}

/* 復原終態（退回 toast 的 undo）：狀態 pill 還原待審、動作鈕解除 disabled。
 * 還原用 applyTerminalState 進終態前記下的原始 class + 文字（各頁初始字不一，
 * 故不可硬寫「待審核」）；fallback 為 admin-pill--pending。MVP 純前端，backend 串接見回報。 */
function undoTerminalState(root) {
  const pill = root.querySelector('.admin-page-head__actions .admin-pill');
  if (pill) {
    const restoreMod = pill.dataset.originalPillClass || 'admin-pill--pending';
    const restoreText = pill.dataset.originalPillText || '待審核';
    ALL_PILL_MODS.forEach((m) => pill.classList.remove(m));
    pill.classList.add(restoreMod);
    pill.textContent = restoreText;
  }
  root.querySelectorAll('.review-action__btn').forEach((btn) => {
    // 進終態時記下的「原本就 disabled」者（下架鈕在未上線狀態）維持 disabled。
    if (btn.dataset.wasDisabled === '1') return;
    btn.disabled = false;
    btn.removeAttribute('aria-disabled');
  });
}

/* ============================ consult 回覆 ============================ */

/** clone 既有回覆卡 markup（沿用 .reply-history__item 結構，textContent 防 XSS）。 */
function buildReplyItem(text) {
  const list = document.querySelector('.reply-history');
  const item = document.createElement('article');
  item.className = 'reply-history__item is-entering';

  const head = document.createElement('header');
  head.className = 'reply-history__head';

  const clinic = document.createElement('span');
  clinic.className = 'reply-history__clinic';
  // 沿用既有卡的診所 icon（複製首張卡的 svg，維持視覺一致）；無則只放文字。
  const existingSvg = list && list.querySelector('.reply-history__clinic svg');
  if (existingSvg) clinic.appendChild(existingSvg.cloneNode(true));
  const clinicName = (list && list.querySelector('.reply-history__clinic'))
    ? list.querySelector('.reply-history__clinic').textContent.trim()
    : '示範診所';
  clinic.appendChild(document.createTextNode(clinicName));

  const time = document.createElement('time');
  time.className = 'reply-history__time';
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const iso = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  time.setAttribute('datetime', iso);
  time.textContent = iso.replace('T', ' ');

  head.appendChild(clinic);
  head.appendChild(time);

  const body = document.createElement('p');
  body.className = 'reply-history__body';
  body.textContent = text; // 純文字插入，無 XSS

  item.appendChild(head);
  item.appendChild(body);

  // 進場 class 在動畫結束後移除（避免殘留 / reduced-motion 下無動畫亦清）
  if (prefersReducedMotion()) {
    item.classList.remove('is-entering');
  } else {
    item.addEventListener('animationend', () => item.classList.remove('is-entering'), { once: true });
  }
  return item;
}

function initConsultReply(root) {
  const form = root.querySelector('.consult-detail__reply-form');
  const list = root.querySelector('.reply-history');
  if (!form || !list) return;

  const textarea = form.querySelector('#reply-body');
  if (!textarea) return;

  textarea.addEventListener('input', () => {
    if (textarea.value.trim().length >= 1) clearInvalid(textarea);
  });

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const text = textarea.value.trim();
    if (text.length < 1) {
      setInvalid(textarea);
      textarea.focus();
      return;
    }
    clearInvalid(textarea);
    list.appendChild(buildReplyItem(text));
    textarea.value = '';
    window.AdminToast.show({
      type: 'success',
      title: '回覆已送出',
      text: '回覆已加入諮詢紀錄。',
    });
  });
}

/* ============================ 內部備註 ============================ */

function initNoteForms(root) {
  // 後台審核詳情頁的內部備註（.review-detail__note-form）+ consult 的處理表單（.consult-detail__process-form）。
  const forms = root.querySelectorAll('.review-detail__note-form, .consult-detail__process-form');
  forms.forEach((form) => {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      window.AdminToast.show({
        type: 'success',
        title: '已儲存',
        text: '內容已儲存（僅內部可見）。',
      });
    });
  });
}

/* ============================ 返回保留篩選（MVP）============================ */

/**
 * 返回列表時保留來源篩選：MVP 讀 sessionStorage 還原 querystring 接回 back 連結。
 * 進站時若 referrer 帶 query（列表頁），存入；back 連結補上。實際篩選持久化待 backend，
 * 此為前端佔位（不影響無 query 時的行為）。
 */
function initBackLink(root) {
  const backLinks = root.querySelectorAll('.admin-detail-back a[href]');
  if (!backLinks.length) return;

  const STORE_KEY = 'admin-review-list-query';
  // 進站：若由列表頁帶 query 進來（document.referrer 同源且帶 ?），記住該 query。
  try {
    const ref = document.referrer;
    if (ref) {
      const refUrl = new URL(ref);
      if (refUrl.origin === window.location.origin && refUrl.search) {
        sessionStorage.setItem(STORE_KEY, refUrl.search);
      }
    }
  } catch (_) { /* referrer 解析失敗忽略，back 連結維持原樣 */ }

  const saved = (() => { try { return sessionStorage.getItem(STORE_KEY); } catch (_) { return null; } })();
  if (!saved) return;

  backLinks.forEach((a) => {
    const href = a.getAttribute('href');
    // 僅當 back 連結本身無 query 時補上（不覆寫既有 query）。
    if (href && !href.includes('?')) {
      a.setAttribute('href', href + saved);
    }
  });
}

/* ============================ 各頁綁定 ============================ */

/**
 * 頁面偵測：用既有 modal id 組合判別。各頁 modal id 不一致（靜態頁歷史），
 * 故逐頁試綁；命中即綁，未命中 no-op。decision modal 一律存在才綁。
 */
function initReviewDetail(root = document) {
  // 早退守門：6 頁皆有 .admin-page-head（後台詳情頁殼）；無則整支 no-op。
  if (!root.querySelector('.review-detail__layout, .consult-detail__layout')) return;

  /* ---- consult-detail：無核准/退回 decision，只有回覆 + 處理表單 ---- */
  initConsultReply(root);

  /* ---- clinic：#approve-modal / #reject-modal（textarea #reject-reason，min 10）---- */
  if (root.querySelector('#reject-modal #reject-reason')) {
    const isClinic = !!root.querySelector('#doc-modal-storefront'); // clinic 專屬燈箱
    const isLicense = !!root.querySelector('#doc-modal-degree'); // license 專屬燈箱
    if (isClinic || isLicense) {
      bindDecision({
        root, modalSel: '#approve-modal', outcome: 'approve',
        toast: { title: '已核准', text: isClinic ? '診所已核准入駐，將於平台掛牌。' : '醫師已標記為已驗證，已通知所屬診所。' },
      });
      bindDecision({
        root, modalSel: '#reject-modal', outcome: 'reject',
        toast: { title: '已退回', text: '已退回並通知對方補正。' },
        validate: {
          textarea: root.querySelector('#reject-reason'),
          min: 10,
        },
      });
    }
  }

  /* ---- data：#approve-modal / #return-modal（textarea #return-reason，min 10）---- */
  if (root.querySelector('#return-modal #return-reason')) {
    bindDecision({
      root, modalSel: '#approve-modal', outcome: 'approve',
      toast: { title: '已核准', text: '變更已套用並對外更新。' },
    });
    bindDecision({
      root, modalSel: '#return-modal', outcome: 'reject',
      toast: { title: '已退回', text: '已退回並通知診所修改。' },
      validate: {
        textarea: root.querySelector('#return-reason'),
        min: 10,
      },
    });
  }

  /* ---- article：#approve-article-modal / #return-article-modal（textarea #return-comment + select #return-reason-type，min 10）---- */
  if (root.querySelector('#return-article-modal #return-comment')) {
    bindDecision({
      root, modalSel: '#approve-article-modal', outcome: 'approve',
      toast: { title: '已通過', text: '文章已通過並發布到前台。' },
    });
    bindDecision({
      root, modalSel: '#return-article-modal', outcome: 'reject',
      toast: { title: '已退回', text: '已退回並通知診所修改。' },
      validate: {
        textarea: root.querySelector('#return-comment'),
        min: 10,
        // 此頁 select 無獨立 error 節點 → field-error null 路徑只標紅框 + focus（不憑空建節點）。
        selectEl: root.querySelector('#return-reason-type'),
      },
    });
    bindUnpublishIfPresent(root, '#unpublish-article-modal', '文章已下架，已從前台移除。');
  }

  /* ---- comment：#approve-comment-modal / #reject-comment-modal（textarea #reject-comment + select #reject-reason-type，min 10）---- */
  if (root.querySelector('#reject-comment-modal #reject-comment')) {
    bindDecision({
      root, modalSel: '#approve-comment-modal', outcome: 'approve',
      toast: { title: '已核准', text: '評論已核准並公開於文章下方。' },
    });
    bindDecision({
      root, modalSel: '#reject-comment-modal', outcome: 'reject',
      toast: { title: '已退回', text: '已退回並通知會員修改。' },
      validate: {
        textarea: root.querySelector('#reject-comment'),
        min: 10,
        // 此頁 select 無獨立 error 節點 → field-error null 路徑只標紅框 + focus（不憑空建節點）。
        selectEl: root.querySelector('#reject-reason-type'),
      },
    });
    bindUnpublishIfPresent(root, '#unpublish-comment-modal', '評論已下架，已從文章下方移除。');
  }

  /* ---- 通用：內部備註 / 處理表單 + 返回保留篩選 ---- */
  initNoteForms(root);
  initBackLink(root);
}

/* 下架 modal 接線（article/comment 預留；本示例狀態下對應動作鈕 disabled，
 * 此 modal 不會被觸發，但仍接線供「已發布/已公開」狀態複用）。 */
function bindUnpublishIfPresent(root, modalSel, toastText) {
  if (!root.querySelector(modalSel)) return;
  bindDecision({
    root, modalSel, outcome: 'unpublish',
    toast: { title: '已下架', text: toastText },
  });
}

/* ============================ auto-init ============================ */

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initReviewDetail());
} else {
  initReviewDetail();
}
